import { useAuth } from "@clerk/clerk-react";
import { Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { VAStatus, VaOrganizationProfileStatus } from "schemas/dashboard";
import { routes } from "schemas/routes";

import StyledVaPage from "components/Styled/VAPage";
import { useAccountReviewState } from "features/Auth/Onboarding/shared/AccountReviewUpsell";
import { fetcherAuth } from "utils/api";
import { useTracking } from "utils/useTracking";

import { SendOption } from "../ReviewAndStartSend";

interface Props {
  finishSetUpLater: () => void;
}

const ReviewMyAccount = (_props: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { trackVaStep } = useTracking();
  const { currentOrg, setCurrentOrg, setOrganizationVaProfile } = useContext(
    OrganizationUserContext,
  );
  const { canSubmitReview } = useAccountReviewState();

  const sendOption = (location.state as { sendOption?: SendOption } | null)
    ?.sendOption;

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canSubmitReview || !sendOption) {
      navigate(`/${routes.reviewSelfServeOnboarding}`, { replace: true });
    }
  }, [canSubmitReview, sendOption, navigate]);

  const approveOrganizationProfile = async ({
    submitReview,
  }: { submitReview?: boolean } = {}) => {
    if (currentOrg?.vaStatus === VAStatus.approved) {
      navigate(`/${routes.virtualAssistant}`);
      return;
    }
    if (!sendOption) {
      return;
    }

    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/approve`,
        "POST",
        {},
        { sendOption },
      );
      if (submitReview && currentOrg?.id) {
        await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/onboarding-review-requests`,
          "POST",
        );
      }
      setCurrentOrg((prev) => {
        if (prev) {
          prev.vaStatus = VAStatus.approved;
        }
        return prev;
      });
      setOrganizationVaProfile((prev) => {
        if (prev) {
          prev.status =
            sendOption === SendOption.now
              ? VaOrganizationProfileStatus.completed
              : VaOrganizationProfileStatus.later;
        }
        return prev;
      });
      navigate(`/${routes.virtualAssistant}`);
    } catch (error) {
      if (error?.message?.includes("follow up question")) {
        setAlert("Please answer follow-up questions for your niches", "error");
        navigate(`/${routes.nichesFollowUpSelfServeOnboarding}`);
        return;
      }
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledVaPage
      title="One last step — submit your account for review 💎"
      description={
        <Typography sx={{ fontSize: { xs: 13, md: 18 }, my: 3 }}>
          Have our Customer Success team review your templates, follow-up
          sequences, profile so you're set up for success — and{" "}
          <strong>boost your reply rate</strong> before you start pitching.
        </Typography>
      }
      time="1"
      body={<></>}
      stepNumber={8}
      totalSteps={8}
      onBack={() => {
        navigate(`/${routes.reviewSelfServeOnboarding}`, {
          state: { sendOption },
        });
        trackVaStep(routes.reviewSelfServeOnboarding);
      }}
      buttonBack="Back"
      buttonNext="Review My Account"
      onNext={() => approveOrganizationProfile({ submitReview: true })}
      onFinishSetUpLater={() => approveOrganizationProfile()}
      finishSetUpLaterLabel="Skip"
      loading={loading}
    />
  );
};

export default ReviewMyAccount;
